import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useLocation, Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  getPaymentAuthenticationRequired,
  isApiError,
  isTerminalPaymentAttemptError,
  programApi,
  updatePaymentAuthenticationForError,
  type ProgramEnrollBody,
} from "@bw/shared/api";
import {
  formatMoney,
  reconcileSavedPaymentMethodSelection,
} from "@bw/shared/utils";
import type {
  ProgramTrackResponse,
  EnrollmentModeQuoteResponse,
  UnifiedEnrollmentResponse,
  UnifiedProgramResponse,
} from "@bw/shared/types/generated";
import { paymentQueryKeys, useProgramPreviewIdentity } from "@bw/shared/hooks";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Page } from "../../../common/components/Page";
import { PaymentMethodsLoadError } from "../../../common/components/PaymentMethodsLoadError";
import {
  MaterialAddCardForm,
  MaterialConnectedElementsProvider,
  MaterialPaymentIntentAuthentication,
  MaterialPaymentMethodPicker,
} from "../../../common/components/payment";
import { useEnroll } from "../hooks/useEnroll";
import { useEnrollmentQuote } from "../hooks/useEnrollmentQuote";
import { useProgramSessions } from "../hooks/useProgramSessions";
import { useLearningSpace } from "../hooks/useLearningSpace";
import {
  useClassPaymentMethods,
  useClassFrontendConfig,
  useClassSetupIntent,
} from "../hooks/usePayment";
import { titleCase, isAgeGated } from "../components/classDisplay";
import {
  CommercialFeeReview,
  hasReadyCommercialReview,
  useCommercialReviewReady,
} from "../components/CommercialFeeReview";

/**
 * Surface 4 — enroll in a track (/classes/:facilityId/:programId/enroll/:trackId).
 *
 * The backend owns every decision; the page only forwards inputs and renders the
 * server's answer:
 *   - PAYMENT is requested only after the server determines an enrollment is
 *     not covered by the member's entitlement. A quoted non-zero price is not
 *     enough to make that decision: entitlement usage is mutable and must be
 *     consumed atomically in the enrollment transaction. A free track or a
 *     full track (→ waitlisted free) also collects no card.
 *   - AGE is collected only when the track is age-gated; the value is sent as-is.
 *     The FE never pre-gates eligibility — an out-of-band age is rejected by the
 *     server and surfaced here.
 *   - The confirmation reads `status` from the response verbatim (enrolled vs
 *     waitlisted) — never a client guess.
 *   - Each enroll attempt is a fresh charge. A hard 4xx failure (declined,
 *     duplicate, age) leaves no charge, so a retry is a genuinely separate
 *     attempt (no stale dedupe).
 */

/** True when the server's authoritative enrollment attempt needs a card. */
function isCardRequired(error: unknown): boolean {
  if (!isApiError(error)) return false;
  if (error.code === "PAYMENT_METHOD_REQUIRED") return true;
  return /stripe_payment_method_id|payment.?method|card (is )?required|non-?zero fee|paymentrequired/i.test(
    `${error.code ?? ""} ${error.message ?? ""}`,
  );
}

export function EnrollPage() {
  const { facilityId, programId, trackId } = useParams<{
    facilityId: string;
    programId: string;
    trackId: string;
  }>();
  const identity = useProgramPreviewIdentity();
  const client = useQueryClient();
  const scopeToken = JSON.stringify([identity, facilityId, programId, trackId]);
  const activeScope = useRef(scopeToken);
  activeScope.current = scopeToken;
  const [deniedScope, setDeniedScope] = useState<string | null>(null);
  const programKey = [
    "program-preview",
    identity,
    "classes",
    programId,
    facilityId,
    "enrollment-program",
  ];
  const trackKey = [
    "program-preview",
    identity,
    "classes",
    programId,
    facilityId,
    "enrollment-track",
    trackId,
  ];
  const programQ = useQuery({
    queryKey: programKey,
    queryFn: async ({ signal }) => {
      const program = await programApi.getProgram(programId as string);
      if (signal.aborted)
        throw new DOMException("Class request was superseded", "AbortError");
      return program;
    },
    enabled: Boolean(programId),
    retry: false,
  });
  const routeMatchesProgram =
    programQ.data?.program_id === programId &&
    programQ.data?.facility_id === facilityId;
  const trackQ = useQuery({
    queryKey: trackKey,
    queryFn: ({ signal }) =>
      programApi.getTrack(programId as string, trackId as string, signal),
    enabled: Boolean(
      programId &&
        trackId &&
        routeMatchesProgram &&
        programQ.isSuccess &&
        programQ.fetchStatus === "idle",
    ),
    retry: false,
  });
  useEffect(
    () => () => {
      for (const queryKey of [programKey, trackKey]) {
        void client.cancelQueries({ queryKey, exact: true });
        client.removeQueries({ queryKey, exact: true });
      }
    },
    [client, identity, programId, facilityId, trackId],
  );
  const track =
    trackQ.data?.program_id === programId && trackQ.data?.track_id === trackId
      ? trackQ.data
      : undefined;
  const accessDenied =
    deniedScope === scopeToken ||
    [programQ.error, trackQ.error].some(
      (error) => isApiError(error) && [401, 403].includes(error.status),
    );
  const canUseCurrentOwner = () => {
    if (activeScope.current !== scopeToken || accessDenied) return false;
    const currentProgram = client.getQueryState(programKey);
    const currentTrack = client.getQueryState(trackKey);
    return (
      currentProgram?.status === "success" &&
      currentProgram.fetchStatus === "idle" &&
      !currentProgram.isInvalidated &&
      currentProgram.data === programQ.data &&
      currentTrack?.status === "success" &&
      currentTrack.fetchStatus === "idle" &&
      !currentTrack.isInvalidated &&
      currentTrack.data === trackQ.data
    );
  };
  const ownerIsCurrent = canUseCurrentOwner();
  useEffect(() => {
    if (!accessDenied) return;
    const prefix = ["program-preview", identity, "classes", programId];
    void client.cancelQueries({ queryKey: prefix });
    client.removeQueries({ queryKey: prefix });
  }, [accessDenied, client, identity, programId]);

  if (
    (!programQ.data && programQ.isLoading) ||
    (routeMatchesProgram && !track && trackQ.isLoading)
  ) {
    return (
      <Page>
        <Stack
          direction="row"
          spacing={1.5}
          role="status"
          sx={{ alignItems: "center" }}
        >
          <CircularProgress size={20} />
          <Typography color="text.secondary">Loading…</Typography>
        </Stack>
      </Page>
    );
  }

  if (accessDenied || !programQ.data) {
    return (
      <Page>
        <Alert severity="error">
          Couldn&apos;t load this enrollment option. Please try again.
        </Alert>
      </Page>
    );
  }

  if (!routeMatchesProgram) {
    return (
      <Page>
        <Alert severity="error">
          This enrollment option is not available at this facility.
        </Alert>
      </Page>
    );
  }

  if (!track) {
    return (
      <Page>
        <Alert severity="error">
          Couldn&apos;t load this enrollment option. Please try again.
        </Alert>
      </Page>
    );
  }

  return (
    <EnrollForm
      key={`${identity}:${facilityId}:${programId}:${trackId}`}
      facilityId={facilityId as string}
      programId={programId as string}
      trackId={trackId as string}
      track={track}
      program={programQ.data}
      ownerIsCurrent={ownerIsCurrent}
      canUseCurrentOwner={canUseCurrentOwner}
      ownerReadFailed={programQ.isError || trackQ.isError}
      retryOwner={() => {
        if (activeScope.current !== scopeToken) return;
        void Promise.all([programQ.refetch(), trackQ.refetch()]);
      }}
      onAccessDenied={() => {
        if (activeScope.current === scopeToken) setDeniedScope(scopeToken);
      }}
    />
  );
}

function EnrollForm({
  facilityId,
  programId,
  trackId,
  track,
  program,
  ownerIsCurrent,
  canUseCurrentOwner,
  ownerReadFailed,
  retryOwner,
  onAccessDenied,
}: {
  facilityId: string;
  programId: string;
  trackId: string;
  track: ProgramTrackResponse;
  program: UnifiedProgramResponse;
  ownerIsCurrent: boolean;
  canUseCurrentOwner: () => boolean;
  ownerReadFailed: boolean;
  retryOwner: () => void;
  onAccessDenied: () => void;
}) {
  const ageGated = isAgeGated(track);
  const { search } = useLocation();
  const skillLabel =
    track.skill_level === "all" ? "All levels" : titleCase(track.skill_level);
  const allowedModes = (track.enrollment_modes ?? []).length
    ? (track.enrollment_modes ?? [])
    : ["full_course"];
  const [enrollmentMode, setEnrollmentMode] = useState(
    allowedModes[0] ?? "full_course",
  );
  // A course's aggregate availability is not an availability result for a
  // particular drop-in occurrence. The server locks and checks the selected
  // session at enrollment time, so never suppress a drop-in just because the
  // course-wide cohort is full.
  const hasSeat =
    enrollmentMode === "session_drop_in" || track.available_spots > 0;
  const [occurrenceStart, setOccurrenceStart] = useState<string | null>(null);
  const sessionsQ = useProgramSessions(
    programId,
    ownerIsCurrent &&
      track.status === "active" &&
      enrollmentMode === "session_drop_in",
    trackId,
  );
  const selectableSessions =
    sessionsQ.data?.items
      .map((item) => item.session)
      .filter((session) => session.status !== "cancelled") ?? [];
  const quoteQ = useEnrollmentQuote(
    trackId,
    {
      enrollment_mode: enrollmentMode as
        | "full_course"
        | "remaining_course"
        | "session_drop_in",
      ...(occurrenceStart ? { occurrence_start: occurrenceStart } : {}),
    },
    ownerIsCurrent && track.status === "active",
  );
  // Membership coverage is decided during the server transaction, not when a
  // quote is rendered. Start without the wallet so a covered player can enroll
  // with no saved card; retain this state after a server-authoritative card
  // requirement so their retry is an ordinary paid enrollment.
  const [paymentRequiredByAttempt, setPaymentRequiredByAttempt] =
    useState(false);

  // Visible age-band hint that avoids the word "age" (the input carries it via
  // aria-label) so the hint never competes with a backend age error for the
  // same on-screen text.
  const bandHint =
    track.age_min != null && track.age_max != null
      ? `${track.age_min}–${track.age_max} yrs`
      : track.age_min != null
        ? `${track.age_min}+ yrs`
        : track.age_max != null
          ? `up to ${track.age_max} yrs`
          : null;

  const paymentSelectionContext = `${facilityId}:${trackId}`;
  const [paymentSelection, setPaymentSelection] = useState(() => ({
    context: paymentSelectionContext,
    paymentMethodId: null as string | null,
  }));
  const selectedPmId =
    paymentSelection.context === paymentSelectionContext
      ? paymentSelection.paymentMethodId
      : null;
  const selectPaymentMethod = useCallback(
    (paymentMethodId: string | null) => {
      setPaymentSelection({
        context: paymentSelectionContext,
        paymentMethodId,
      });
    },
    [paymentSelectionContext],
  );
  const initializedPaymentContextRef = useRef<string | null>(null);
  const [cardSetupBusy, setCardSetupBusy] = useState(false);
  const [age, setAge] = useState("");
  const [result, setResult] = useState<UnifiedEnrollmentResponse | null>(null);
  const [paymentAuthentication, setPaymentAuthentication] = useState<{
    clientSecret: string;
    body: ProgramEnrollBody;
  } | null>(null);
  const [submittedPayment, setSubmittedPayment] = useState<{
    body: ProgramEnrollBody;
    quote: EnrollmentModeQuoteResponse;
  } | null>(null);
  // A submitted attempt owns its recovery phase. A refreshed price or our
  // own reserved last seat must not remove SCA or replace the original body.
  const needsPayment =
    submittedPayment !== null ||
    paymentAuthentication !== null ||
    (hasSeat &&
      Number(quoteQ.data?.amount ?? 0) > 0 &&
      paymentRequiredByAttempt);
  const reviewReady = useCommercialReviewReady(quoteQ.data);
  const displayedQuote = submittedPayment?.quote ?? quoteQ.data;

  const enroll = useEnroll(trackId);

  // Payment spine — only fetched when payment is actually required. Uses the
  // classes-local wrappers (useClass*) NOT the shared payment hooks: the classes
  // claim flow gates on `enabled` alone (no facilityId guard) — see usePayment.ts
  // for why. The wrappers key into the SHARED `paymentQueryKeys` cache slot.
  const methodsQ = useClassPaymentMethods(facilityId, needsPayment);
  const configQ = useClassFrontendConfig(facilityId, needsPayment);
  const setupIntent = useClassSetupIntent();
  useEffect(() => {
    if (
      [quoteQ.error, methodsQ.error, configQ.error].some(
        (error) => isApiError(error) && [401, 403].includes(error.status),
      )
    )
      onAccessDenied();
  }, [quoteQ.error, methodsQ.error, configQ.error, onAccessDenied]);
  const paymentLocked =
    enroll.isPending || paymentAuthentication !== null || cardSetupBusy;

  const createSetupIntentSecret = useCallback(
    async (idempotencyKey: string) => {
      const res = await setupIntent.mutateAsync(idempotencyKey);
      return res.client_secret;
    },
    [setupIntent],
  );

  const methods = methodsQ.data ?? [];
  const paymentMethodsSettled =
    needsPayment &&
    methodsQ.data !== undefined &&
    !methodsQ.isFetching &&
    !methodsQ.isError;
  useEffect(() => {
    if (!paymentMethodsSettled || methodsQ.data === undefined) return;
    const initializeProviderDefault =
      initializedPaymentContextRef.current !== paymentSelectionContext;
    initializedPaymentContextRef.current = paymentSelectionContext;
    setPaymentSelection((current) => {
      const currentId =
        current.context === paymentSelectionContext
          ? current.paymentMethodId
          : null;
      const paymentMethodId = reconcileSavedPaymentMethodSelection(
        methodsQ.data,
        currentId,
        initializeProviderDefault,
      );
      if (
        current.context === paymentSelectionContext &&
        current.paymentMethodId === paymentMethodId
      ) {
        return current;
      }
      return { context: paymentSelectionContext, paymentMethodId };
    });
  }, [methodsQ.data, paymentMethodsSettled, paymentSelectionContext]);
  const chargeReadyPmId =
    paymentMethodsSettled && methodsQ.data !== undefined
      ? reconcileSavedPaymentMethodSelection(methodsQ.data, selectedPmId, false)
      : null;

  function handleEnroll() {
    if (paymentLocked) return;
    if (!submittedPayment && !canUseCurrentOwner()) return;
    if (
      !submittedPayment &&
      enrollmentMode === "session_drop_in" &&
      (!sessionsQ.ready ||
        !selectableSessions.some(
          (session) => session.occurrence_start === occurrenceStart,
        ))
    )
      return;
    if (
      needsPayment &&
      !submittedPayment &&
      (!chargeReadyPmId || !hasReadyCommercialReview(quoteQ.data))
    )
      return;
    const body: ProgramEnrollBody = {};
    body.enrollment_mode =
      enrollmentMode as ProgramEnrollBody["enrollment_mode"];
    if (quoteQ.data?.quote_id) body.enrollment_quote_id = quoteQ.data.quote_id;
    if (quoteQ.data?.commercial_fee?.readiness === "ready")
      body.commercial_fee_quote_id = quoteQ.data.commercial_fee.fee_quote_id;
    if (occurrenceStart) body.occurrence_start = occurrenceStart;
    if (ageGated) {
      const parsed = Number.parseInt(age, 10);
      if (Number.isFinite(parsed)) body.student_age = parsed;
    }
    if (needsPayment && chargeReadyPmId) {
      body.stripe_payment_method_id = chargeReadyPmId;
    }
    const submitted = submittedPayment?.body ?? body;
    if (submitted.stripe_payment_method_id && !submittedPayment && quoteQ.data)
      setSubmittedPayment({ body: submitted, quote: quoteQ.data });
    enroll.mutate(submitted, {
      onSuccess: (res) => {
        setSubmittedPayment(null);
        setResult(res);
      },
      onError: (error) => {
        if (isApiError(error) && [401, 403].includes(error.status)) {
          onAccessDenied();
          return;
        }
        if (isCardRequired(error) && !submitted.stripe_payment_method_id) {
          setPaymentRequiredByAttempt(true);
          return;
        }
        const authentication = getPaymentAuthenticationRequired(error);
        if (authentication) {
          setPaymentAuthentication({
            clientSecret: authentication.clientSecret,
            body: submitted,
          });
        } else if (isTerminalPaymentAttemptError(error)) {
          setSubmittedPayment(null);
          void quoteQ.refetch();
        }
      },
    });
  }

  async function finishPaymentAuthentication(
    authenticatedClientSecret: string,
  ) {
    const authentication = paymentAuthentication;
    if (
      !authentication ||
      authentication.clientSecret !== authenticatedClientSecret
    ) {
      return;
    }
    try {
      const response = await enroll.mutateAsync(authentication.body);
      setPaymentAuthentication((current) =>
        current?.clientSecret === authenticatedClientSecret ? null : current,
      );
      setResult(response);
      setSubmittedPayment(null);
    } catch (error) {
      if (isApiError(error) && [401, 403].includes(error.status)) {
        onAccessDenied();
        throw error;
      }
      setPaymentAuthentication((current) =>
        updatePaymentAuthenticationForError(
          current,
          authenticatedClientSecret,
          error,
        ),
      );
      if (isTerminalPaymentAttemptError(error)) {
        setSubmittedPayment(null);
        void quoteQ.refetch();
      }
      throw error;
    }
  }

  // ---- Success confirmation — status STRAIGHT from the response ---------------
  if (
    !result &&
    !submittedPayment &&
    !paymentAuthentication &&
    !ownerIsCurrent
  ) {
    return (
      <Page>
        {ownerReadFailed ? (
          <Alert
            severity="error"
            action={
              <Button size="small" onClick={retryOwner}>
                Retry enrollment information
              </Button>
            }
          >
            Enrollment information unavailable right now.
          </Alert>
        ) : (
          <Typography role="status">
            Refreshing enrollment information…
          </Typography>
        )}
      </Page>
    );
  }
  if (
    !result &&
    !submittedPayment &&
    !paymentAuthentication &&
    track.status !== "active"
  ) {
    return (
      <Page>
        <Alert severity="info">Enrollment is closed for this option.</Alert>
      </Page>
    );
  }
  if (result) {
    const waitlisted = result.status === "waitlisted";
    return (
      <Page title="Enrollment">
        <Stack spacing={1.5} role="status" sx={{ alignItems: "flex-start" }}>
          {waitlisted ? (
            <>
              <Typography component="h2" variant="h6">
                You&apos;re on the waitlist
              </Typography>
              <Typography>
                This option is full — we&apos;ll let you know if a spot opens up
                for you.
              </Typography>
            </>
          ) : (
            <>
              <Typography component="h2" variant="h6">
                You&apos;re enrolled
              </Typography>
              <Typography>
                Your place is reserved
                {result.fee_paid != null
                  ? ` — ${formatMoney(result.fee_paid, result.currency)} paid`
                  : ""}
                .
              </Typography>
            </>
          )}
          <Button
            component={RouterLink}
            variant="outlined"
            size="small"
            to="/me/classes"
          >
            View my classes
          </Button>
          {result.enrollment_mode !== "session_drop_in" ? (
            <LearningSpaceLink programId={programId} />
          ) : null}
        </Stack>
      </Page>
    );
  }

  const err = enroll.error;
  const errMessage =
    paymentAuthentication || isCardRequired(err)
      ? null
      : isApiError(err)
        ? err.message
        : err
          ? "Something went wrong. Please try again."
          : null;

  const ctaLabel = needsPayment
    ? enroll.isPending
      ? "Enrolling…"
      : submittedPayment
        ? "Finish request"
        : "Pay & enroll"
    : !hasSeat
      ? "Join the waitlist"
      : enroll.isPending
        ? "Enrolling…"
        : "Enroll";

  return (
    <Page title="Enroll">
      <Stack spacing={3}>
        <Stack component="header" spacing={0.75}>
          <Typography variant="overline" color="primary.main">
            {program.name}
          </Typography>
          <Typography component="h2" variant="h5">
            {track.name}
          </Typography>
          {skillLabel &&
          track.name.trim().toLowerCase() !== skillLabel.toLowerCase() ? (
            <Typography variant="body2" color="text.secondary">
              {track.skill_level === "all"
                ? skillLabel
                : `Level: ${skillLabel}`}
            </Typography>
          ) : null}
          {quoteQ.isLoading ? (
            <Typography color="text.secondary">
              Calculating your enrollment…
            </Typography>
          ) : displayedQuote ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: "baseline" }}>
              {displayedQuote.campaign ? (
                <Typography
                  component="del"
                  aria-label={`Original price ${formatMoney(
                    displayedQuote.subtotal_amount,
                    displayedQuote.currency,
                  )}`}
                  color="text.secondary"
                >
                  {formatMoney(
                    displayedQuote.subtotal_amount,
                    displayedQuote.currency,
                  )}
                </Typography>
              ) : null}
              <Typography sx={{ fontWeight: 800 }}>
                {displayedQuote.campaign ? "Special price " : ""}
                {formatMoney(displayedQuote.amount, displayedQuote.currency)}
              </Typography>
            </Stack>
          ) : null}
          {displayedQuote?.campaign ? (
            <Alert severity="success" icon={false} role="note">
              <strong>{displayedQuote.campaign.name}</strong> applied — save{" "}
              {formatMoney(
                displayedQuote.campaign.discount,
                displayedQuote.currency,
              )}
            </Alert>
          ) : null}
          {!hasSeat && !submittedPayment ? (
            <Alert severity="info" role="note">
              This option is full — enrolling adds you to the waitlist at no
              charge.
            </Alert>
          ) : null}
        </Stack>

        <CommercialFeeReview quote={displayedQuote} required={needsPayment} />
        {quoteQ.isError && !submittedPayment ? (
          <Alert severity="error">
            <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
              <Typography variant="body2">
                {isApiError(quoteQ.error) && quoteQ.error.status === 422
                  ? "This enrollment option is unavailable. Return to the class to check the available options."
                  : "We couldn’t load your enrollment price and details. Try again before continuing."}
              </Typography>
              {isApiError(quoteQ.error) && quoteQ.error.status === 422 ? (
                <Button
                  component={RouterLink}
                  to={`/classes/${facilityId}/${programId}${search}`}
                  size="small"
                  sx={{ minHeight: 44 }}
                >
                  View class options
                </Button>
              ) : (
                <Button
                  size="small"
                  disabled={
                    paymentLocked || quoteQ.isFetching || !ownerIsCurrent
                  }
                  onClick={() => {
                    if (!paymentLocked && ownerIsCurrent) void quoteQ.refetch();
                  }}
                  sx={{ minHeight: 44 }}
                >
                  {quoteQ.isFetching ? "Retrying…" : "Retry enrollment details"}
                </Button>
              )}
            </Stack>
          </Alert>
        ) : null}
        {needsPayment && !quoteQ.isError && !reviewReady ? (
          <Button
            size="small"
            disabled={
              paymentLocked ||
              submittedPayment !== null ||
              quoteQ.isFetching ||
              !ownerIsCurrent
            }
            onClick={() => {
              if (!submittedPayment && !paymentLocked && ownerIsCurrent)
                void quoteQ.refetch();
            }}
          >
            Refresh payment review
          </Button>
        ) : null}

        <FormControl
          size="small"
          disabled={paymentLocked || submittedPayment !== null || !hasSeat}
        >
          <InputLabel id="enrollment-mode-label">Enrollment option</InputLabel>
          <Select
            labelId="enrollment-mode-label"
            label="Enrollment option"
            value={enrollmentMode}
            onChange={(event) => {
              setEnrollmentMode(event.target.value);
              setOccurrenceStart(null);
              setPaymentRequiredByAttempt(false);
            }}
          >
            {allowedModes.map((mode) => (
              <MenuItem key={mode} value={mode}>
                {mode === "full_course"
                  ? "Full course"
                  : mode === "remaining_course"
                    ? "Join remaining sessions"
                    : "Single-session drop-in"}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {enrollmentMode === "session_drop_in" ? (
          <Stack spacing={1.5}>
            {sessionsQ.isError ? (
              <Typography role="status" color="text.secondary">
                Schedule unavailable right now.
              </Typography>
            ) : sessionsQ.ready && selectableSessions.length === 0 ? (
              <Typography color="text.secondary">
                {sessionsQ.canNext
                  ? "More sessions remain"
                  : "No upcoming sessions in this window."}
              </Typography>
            ) : null}
            <FormControl
              size="small"
              disabled={
                paymentLocked || submittedPayment !== null || !sessionsQ.ready
              }
            >
              <InputLabel id="drop-in-session-label">Session</InputLabel>
              <Select
                labelId="drop-in-session-label"
                label="Session"
                value={
                  selectableSessions.some(
                    (session) => session.occurrence_start === occurrenceStart,
                  )
                    ? occurrenceStart
                    : ""
                }
                onChange={(event) =>
                  setOccurrenceStart(event.target.value || null)
                }
              >
                {selectableSessions.map((session) => (
                  <MenuItem
                    key={session.occurrence_start}
                    value={session.occurrence_start}
                  >
                    {new Date(session.start_time).toLocaleString()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                disabled={
                  paymentLocked ||
                  submittedPayment !== null ||
                  !sessionsQ.ready ||
                  !sessionsQ.canPrevious
                }
                onClick={() => {
                  setOccurrenceStart(null);
                  sessionsQ.previous();
                }}
              >
                Previous sessions
              </Button>
              <Button
                size="small"
                disabled={
                  paymentLocked ||
                  submittedPayment !== null ||
                  !sessionsQ.ready ||
                  !sessionsQ.canNext
                }
                onClick={() => {
                  setOccurrenceStart(null);
                  sessionsQ.next();
                }}
              >
                Next sessions
              </Button>
              <Button
                size="small"
                disabled={
                  paymentLocked ||
                  submittedPayment !== null ||
                  sessionsQ.isLoading
                }
                onClick={() => {
                  setOccurrenceStart(null);
                  sessionsQ.restart();
                }}
              >
                Restart preview
              </Button>
            </Stack>
          </Stack>
        ) : null}

        {displayedQuote ? (
          <Stack spacing={0.5}>
            <Typography variant="body2" color="text.secondary">
              Includes {displayedQuote.entitled_sessions.length} session
              {displayedQuote.entitled_sessions.length === 1 ? "" : "s"}.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {displayedQuote.learning_space_included
                ? displayedQuote.learning_space_archive_at
                  ? `Includes the class Learning Space. Learner posting becomes read-only on ${new Date(displayedQuote.learning_space_archive_at).toLocaleString()}.`
                  : "Includes the class Learning Space through its posted archive date."
                : "The class Learning Space is not included with this enrollment."}
            </Typography>
          </Stack>
        ) : null}

        {/* Age — only for age-gated tracks. The FE never pre-blocks an
            out-of-band value; the server validates and rejects it. The input's
            accessible name carries "age" via aria-label; the visible caption
            avoids the word so it never competes with a backend age error for
            the same on-screen text. */}
        {ageGated ? (
          <TextField
            id="student-age"
            label="How old is the student?"
            helperText={bandHint}
            size="small"
            type="number"
            value={age}
            disabled={paymentLocked || submittedPayment !== null}
            onChange={(e) => setAge(e.target.value)}
            slotProps={{
              htmlInput: {
                "aria-label": "Student age",
                inputMode: "numeric",
                min: 0,
              },
            }}
          />
        ) : null}

        {/* Payment — only when the backend will actually charge. */}
        {needsPayment ? (
          <Stack component="section" spacing={2} aria-label="Payment">
            <Alert severity="info">
              Your membership does not cover this enrollment. Choose a payment
              method to continue.
            </Alert>
            {configQ.isError ? (
              <Alert severity="error">
                This facility can&apos;t take card payments yet. Please try
                again later.
              </Alert>
            ) : methodsQ.isError && paymentAuthentication === null ? (
              <PaymentMethodsLoadError
                retrying={methodsQ.isFetching}
                onRetry={() => void methodsQ.refetch()}
              />
            ) : configQ.isLoading || methodsQ.isLoading ? (
              <Stack
                direction="row"
                spacing={1.5}
                role="status"
                sx={{ alignItems: "center" }}
              >
                <CircularProgress size={20} />
                <Typography color="text.secondary">
                  Loading payment options…
                </Typography>
              </Stack>
            ) : (
              <>
                <MaterialPaymentMethodPicker
                  methods={methods}
                  selectedId={selectedPmId}
                  onSelect={selectPaymentMethod}
                  disabled={paymentLocked || submittedPayment !== null}
                  allowRemove
                  facilityId={facilityId}
                  paymentMethodsQueryKey={paymentQueryKeys.paymentMethods(
                    facilityId,
                  )}
                />
                {configQ.data ? (
                  <MaterialConnectedElementsProvider config={configQ.data}>
                    {paymentAuthentication ? (
                      <MaterialPaymentIntentAuthentication
                        clientSecret={paymentAuthentication.clientSecret}
                        onAuthenticated={finishPaymentAuthentication}
                      />
                    ) : (
                      <MaterialAddCardForm
                        publishableKey={configQ.data.publishable_key}
                        createSetupIntent={createSetupIntentSecret}
                        onBusyChange={setCardSetupBusy}
                        onCardAdded={async (pmId) => {
                          const refreshed = await methodsQ.refetch();
                          const savedMethod = refreshed.data?.find(
                            (method) => method.payment_method_id === pmId,
                          );
                          if (
                            refreshed.isError ||
                            savedMethod?.is_usable !== true
                          ) {
                            throw new Error(
                              "The saved card is not available yet. Retry to refresh it.",
                            );
                          }
                          selectPaymentMethod(pmId);
                        }}
                      />
                    )}
                  </MaterialConnectedElementsProvider>
                ) : null}
              </>
            )}
          </Stack>
        ) : null}

        {errMessage ? <Alert severity="error">{errMessage}</Alert> : null}

        {paymentAuthentication ? null : (
          <Button
            variant="contained"
            size="small"
            onClick={handleEnroll}
            disabled={
              paymentLocked ||
              (!submittedPayment &&
                (!ownerIsCurrent ||
                  quoteQ.isLoading ||
                  !quoteQ.data ||
                  (enrollmentMode === "session_drop_in" &&
                    (!sessionsQ.ready ||
                      !selectableSessions.some(
                        (session) =>
                          session.occurrence_start === occurrenceStart,
                      ))) ||
                  (needsPayment && (!chargeReadyPmId || !reviewReady))))
            }
          >
            {ctaLabel}
          </Button>
        )}
      </Stack>
    </Page>
  );
}

function LearningSpaceLink({ programId }: { programId: string }) {
  const learningSpace = useLearningSpace(programId);
  if (learningSpace.isLoading || learningSpace.isError || !learningSpace.data) {
    return null;
  }
  const space = learningSpace.data;
  return (
    <Stack spacing={0.5} sx={{ alignItems: "flex-start" }}>
      <Button
        component={RouterLink}
        variant="outlined"
        size="small"
        to={`/community/communities/${space.community_id}`}
      >
        Open class Learning Space
      </Button>
      <Typography variant="body2" color="text.secondary">
        {space.status === "read_only"
          ? "This Learning Space is read-only. You can still view its resources and history."
          : `Learner posting becomes read-only on ${new Date(space.archive_at).toLocaleString()}.`}
      </Typography>
    </Stack>
  );
}
